import { groupBy, groupByKey, indexBy, indexItemsById } from '@/helpers/IndexHelpers'

import { TixTime } from '@/TixTime/TixTime'
import { API } from '@/api/API'

const dateFormat = 'ddd, MMMM D YYYY'

function deduplicateBundleTickets<T extends { flow_resolution_admit_id?: string | null }>(tickets: T[]): T[] {
  const seen = new Set<string>()
  const result: T[] = []
  for (const t of tickets) {
    const admitId = t.flow_resolution_admit_id
    if (!admitId) {
      result.push(t)
    } else if (!seen.has(admitId)) {
      seen.add(admitId)
      result.push(t)
    }
  }
  return result
}

function byStartDate(a: OrderSession, b: OrderSession) {
  if (a.start_datetime! > b.start_datetime!) return 1
  if (a.start_datetime! < b.start_datetime!) return -1
  return 0
}

interface OrderSession extends SearchResult {
  tickets: StandardTicket[]
  flow_resolution_id: string | null
  /** Flow name when this session is part of a flow; used for display instead of event name */
  flow_name: string | null
}

export function fetchOrderSessions(orderId?: string): Promise<OrderSession[]> {
  return API.getUncached<'search_result' | 'ticket'>(`my/order_session`, {
    embed: 'ticket',
    query: { 'ticket.state._in': 'booked,redeemed' },
  }).then((response) => {
    // Filter out gift cards, donations, memberships, admission passes; any search_result without a start time
    // TODO: Extend the API to support this filtering on search_result queries?
    // There is work planned to include these tickets in My Account in future
    const searchResults = response.search_result._data
      .filter((result) => Boolean(result.start_datetime))
      .filter((result) => (orderId ? result.ticket_order_id === orderId : true))

    const tickets: StandardTicket[] = response.ticket._data
      .filter((ticket) => Boolean(ticket.event_session_id))
      .filter((ticket) => (orderId ? ticket.ticket_order_id === orderId : true)) as StandardTicket[]

    const index: Dict<Dict<StandardTicket[]>> = {}
    for (const ticket of tickets) {
      index[ticket.ticket_order_id!] ??= {}
      index[ticket.ticket_order_id!][ticket.event_session_id!] ??= []
      index[ticket.ticket_order_id!][ticket.event_session_id!].push(ticket)
    }

    return searchResults
      .map((item): OrderSession => {
        const sessionTickets = index[item.ticket_order_id]?.[item.event_session_id] ?? []
        return {
          ...item,
          tickets: sessionTickets,
          flow_resolution_id: item.flow_resolution_id ?? null,
          flow_name: item.flow_name ?? null,
        }
      })
      .filter((item) => item.tickets.length > 0)
  })
}

export function getOrderTotalPrice(id: string): Promise<number> {
  // Don't use the "my" prefix for this call
  // "my/ticket_order" doesn't include "ticket_order_fees" in the response
  return API.getUncached<'ticket_order' | 'ticket_order_fees'>(`ticket_order/${id}`).then((response) => {
    return parseFloat(response.ticket_order_fees._data[0].total_after_fees_price)
  })
}

interface OrderDetail {
  templates: EventTemplate[]
  orders: OrderSession[]
  venues: Venue[]
  price?: number
}

export function getMyOrders(): Promise<OrderDetail | null> {
  return fetchOrderSessions().then(fetchOrderDetails)
}

export function getMyOrder(orders: OrderSession[], id: string): Promise<RecentOrder | null> {
  return fetchOrderDetails(orders).then((orderDetails) => {
    if (!orderDetails) return null
    return getOrderTotalPrice(id)
      .then((price) => {
        return { ...orderDetails, price }
      })
      .then(({ orders, venues, templates, price }) => {
        return formatRecentOrders(orders, venues, templates, price)[id]
      })
  })
}

function fetchOrderDetails(orders: OrderSession[]): Promise<OrderDetail | null> {
  if (!orders.length) return Promise.resolve(null)

  const eventIds = orders.map((order) => order.event_template_id)
  return API.getCached<'event_template' | 'venue'>('event_template', {
    query: { 'id._in': eventIds },
    embed: 'venue',
  }).then((response) => {
    return {
      orders: orders.sort((a, b) => {
        if (a.purchased_on < b.purchased_on) return 1
        if (a.purchased_on > b.purchased_on) return -1
        return 0
      }),
      venues: response.venue._data,
      templates: response.event_template._data,
    }
  })
}
export function formatRecentOrders(
  orderSessions: OrderSession[],
  venues: Venue[],
  templates: EventTemplate[],
  price?: number,
): Dict<RecentOrder> {
  const venuesById = indexItemsById(venues)
  const templatesById = indexItemsById(templates)
  const grouped = groupBy('ticket_order_id', orderSessions)

  return Object.fromEntries(
    Object.entries(grouped).map(([id, sessions]) => {
      const first = sessions[0]
      const template = templatesById[first.event_template_id]
      const timezone = venuesById[template?.venue_id]?.timezone

      const bundleSessions = sessions.filter((s) => s.flow_resolution_id)
      const nonBundleSessions = sessions.filter((s) => !s.flow_resolution_id)

      const bundleTickets = bundleSessions.flatMap((s) => s.tickets)
      const dedupedBundleTickets = deduplicateBundleTickets(bundleTickets)
      const numTickets = dedupedBundleTickets.length + nonBundleSessions.reduce((sum, s) => sum + s.tickets.length, 0)
      const numSessions = new Set(bundleSessions.map((s) => s.flow_resolution_id)).size + nonBundleSessions.length

      return [
        id,
        {
          orderNumber: first.order_number,
          purchaseDate: new TixTime(first.purchased_on, timezone).format(dateFormat),
          numTickets: numTickets,
          numSessions: numSessions,
          ticketOrderId: id,
          totalPrice: price,
        },
      ]
    }),
  )
}

export function getMyUpcomingSessions(): Promise<SessionRow[] | null> {
  return fetchOrderSessions().then((sessions) => {
    const upcoming = filterUpcomingSessions(sessions)
    return upcoming.length > 0 ? getSessionDetails(upcoming) : null
  })
}

function filterUpcomingSessions(sessions: OrderSession[]): OrderSession[] {
  const grouped = groupByKey(sessions, sessionRowKey)
  const result: OrderSession[] = []
  for (const groupSessions of Object.values(grouped)) {
    const hasUpcoming = groupSessions.some((s) => new TixTime(s.start_datetime).isAfter())
    if (hasUpcoming) {
      result.push(...groupSessions)
    }
  }

  return result.sort(byStartDate)
}

export function getMySession(id: string): Promise<SessionRow> {
  return fetchOrderSessions()
    .then((sessions) => sessions.filter((session) => sessionRowKey(session) === id))
    .then(getSessionDetails)
    .then((rows) => rows[0])
}

/** Key for a SessionRow: flow resolution for bundles, event session id for standalone purchases */
function sessionRowKey(session: OrderSession): string {
  return session.flow_resolution_id ?? session.event_session_id
}

export async function getSessionDetails(sessions: OrderSession[]): Promise<SessionRow[]> {
  const grouped = groupByKey(sessions, sessionRowKey)
  const eventIds = sessions.map((session) => session.event_template_id)
  const response = await API.getCached<'event_template' | 'venue' | 'meta'>('event_template', {
    query: { 'id._in': eventIds.join(',') },
    embed: 'meta,venue',
  })

  const profileImages = indexBy(
    'resource_id',
    response.meta._data.filter((metaItem) => {
      return metaItem.metakey === 'image_profile' && metaItem.resource === 'event_template'
    }),
  )
  const venues = indexItemsById(response.venue._data)
  const templates = indexItemsById(response.event_template._data)

  return Object.entries(grouped).map(([id, groupSessions]) => {
    const first = groupSessions[0]
    const template = templates[first.event_template_id]
    const timezone = venues[template.venue_id].timezone
    const isBundle = Boolean(first.flow_resolution_id)

    const sortedByDate = [...groupSessions].sort(byStartDate)
    const allTickets = groupSessions.flatMap((s) => s.tickets)
    const ticketsForDisplay = isBundle ? deduplicateBundleTickets(allTickets) : allTickets
    const hasUpcoming = sortedByDate.some((s) => new TixTime(s.start_datetime, timezone).isAfter())

    const uniqueDateTimes = [...new Set(sortedByDate.map((s) => s.start_datetime))]
    const timesByDate: Record<string, string[]> = {}
    for (const dateTime of uniqueDateTimes) {
      const time = new TixTime(dateTime, timezone)
      const dateKey = time.format(dateFormat)
      if (!timesByDate[dateKey]) timesByDate[dateKey] = []
      timesByDate[dateKey].push(time.format('LT'))
    }
    const dateTimeEntries: SessionRowDateEntry[] = Object.entries(timesByDate).map(([date, times]) => ({
      date,
      time: times.join(', '),
    }))

    return {
      id,
      name: first.flow_name ?? first.event_name,
      dateTimeEntries,
      ticketQuantity: ticketsForDisplay.length,
      // TODO: Niether of the endpoints we use for My Account can embed flow metadata yet; Flow rows do not render images
      // Update this when the API supports what we need to display flow images.
      image: profileImages[first.event_template_id]?.value ?? '',
      orderIds: [...new Set(groupSessions.map((s) => s.ticket_order_id))],
      eventSessionIds: groupSessions.map((s) => s.event_session_id),
      isUpcoming: hasUpcoming,
    }
  })
}

interface TicketOrderResponse {
  tickets: TicketRow[]
  orders: TicketOrder[]
}

export function fetchTicketOrders(session: SessionRow): Promise<TicketOrderResponse> {
  return API.getUncached<'ticket_order' | 'ticket_group' | 'ticket_type' | 'ticket'>(`my/ticket_order`, {
    query: { 'id._in': session.orderIds, 'ticket.state._in': 'booked,redeemed' },
    embed: 'ticket,ticket_type,ticket_group',
  }).then((response) => {
    const groups = indexItemsById(response.ticket_group._data)
    const types = indexItemsById(response.ticket_type._data)
    const tickets = groupBy('ticket_order_id', response.ticket._data)

    const result: TicketOrderResponse = {
      tickets: [],
      orders: [],
    }

    response.ticket_order._data.forEach((order) => {
      const orderTickets = tickets[order.id].filter((ticket) =>
        session.eventSessionIds.includes(ticket.event_session_id!),
      )
      const ticketsForSession = deduplicateBundleTickets(orderTickets)
      const sessionTickets: TicketRow[] = ticketsForSession.map((ticket) => ({
        group: groups[ticket.ticket_group_id].name,
        type: types[ticket.ticket_type_id].name,
        code: ticket.scan_code,
      }))
      result.tickets = result.tickets.concat(sessionTickets)
      result.orders = result.orders.concat(order)
    })
    return result
  })
}
